import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { BackButton, CustomPagination, SkeletonTable } from '../components';
import AlertNotification from '../components/common/AlertNotification';
import { FiAward, FiSearch, FiUser } from 'react-icons/fi';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: 'bold',
    },
  },
};

const GraduatedStudents = ({ history }) => {
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState({ type: '', message: '' });

  useEffect(() => {
    if (schoolId) {
      fetchGraduatedStudents();
    }
  }, [schoolId]);

  const fetchGraduatedStudents = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(`/children?school_id=${schoolId}&academic_status=graduated&limit=500`);
      setStudents(data.children || data.data || []);
    } catch {
      setAlert({ type: 'error', message: 'Failed to fetch graduated students.' });
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(s =>
    s.firstname?.toLowerCase().includes(filterText.toLowerCase()) ||
    s.lastname?.toLowerCase().includes(filterText.toLowerCase()) ||
    s.registration_id?.toLowerCase().includes(filterText.toLowerCase())
  ).sort((a, b) => (a.lastname || '').localeCompare(b.lastname || ''));

  // A graduated student has no current class (class_id is null once they
  // graduate) — StudentProfile.jsx never dereferences classId, it just
  // forwards it as a query-string filter, and the Result tab's own class
  // picker lets the user select the class results were recorded against.
  const openProfile = (row) => {
    history.push(`/students/view/${row._id}/none`, {
      student: [row.lastname, row.firstname, row.middlename].filter(Boolean).join(' '),
      tab: 'result',
    });
  };

  const columns = [
    {
      name: 'Admission No',
      selector: row => row.registration_id || '-',
      sortable: true,
    },
    {
      name: 'Surname',
      selector: row => row.lastname,
      sortable: true,
      cell: row => <span className="font-semibold">{row.lastname}</span>,
    },
    {
      name: 'First Name',
      selector: row => row.firstname,
      sortable: true,
    },
    {
      name: 'Middle Name',
      selector: row => row.middlename || '-',
    },
    {
      name: 'Gender',
      selector: row => row.gender || '-',
    },
    {
      name: 'Actions',
      cell: row => (
        <button
          type="button"
          onClick={() => openProfile(row)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
        >
          <FiUser className="w-3 h-3" />
          View Profile
        </button>
      ),
      width: '150px',
    },
  ];

  return (
    <div className="m-2 mt-20 md:mx-10 md:my-0 py-2 md:py-7">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-10 min-h-screen">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <BackButton history={history} />
          <div className="h-5 w-px bg-gray-200" />
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest">Students</p>
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Graduated Students</h1>
          </div>
        </div>

        {/* Alert */}
        {alert.message && (
          <div className="mb-4">
            <AlertNotification
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert({ type: '', message: '' })}
            />
          </div>
        )}

        {/* Banner */}
        {!loading && (
          <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl mb-6">
            <FiAward className="text-emerald-500 text-xl shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                {students.length} Graduated Student{students.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-emerald-600">
                Students who have completed their final class and graduated.
              </p>
            </div>
          </div>
        )}

        {/* Table card */}
        <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <div className="relative max-w-xs">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or admission no..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredStudents}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
            noDataComponent={
              <div className="flex flex-col items-center py-16 text-center">
                <FiAward className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-500">No graduated students found</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default GraduatedStudents;
